import { useQuery } from "@tanstack/react-query";
import { useApiFetcher } from "../ApiProvider";
import { QUERY_KEYS } from "../queryKeys";

/**
 * Polls the server for the version of the build currently being served, so a
 * long-running tab can be told a release shipped.
 *
 * Refetching on focus is the part that matters. The member who needs this is
 * the one who left Sukha open, went somewhere else, and came back — waiting out
 * a fixed interval before telling them means they carry on in a stale build for
 * up to that long after they return. A backgrounded tab still does not poll
 * (`refetchIntervalInBackground: false`); there is nobody there to read a toast.
 *
 * @param pollIntervalMs - Polling interval in ms (default: 5 minutes)
 */
export function useAppVersion(pollIntervalMs = 5 * 60_000) {
  const apiFetcher = useApiFetcher();
  return useQuery<string | null>({
    queryKey: QUERY_KEYS.APP_VERSION,
    queryFn: async () => {
      try {
        const data = await apiFetcher<{
          version?: string | null;
        }>("/api/v1/settings/version");
        return data?.version ?? null;
      } catch {
        return null;
      }
    },
    refetchInterval: pollIntervalMs,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    staleTime: 0
  });
}